/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";

import { getButtonStyles, getIconStyle } from "./styles";

import type { ButtonColorScheme } from "./types/ButtonColorScheme";
import type { ButtonSize } from "./types/ButtonSize";
import type { ButtonVariant } from "./types/ButtonVariant";
import type { SerializedStyles } from "@emotion/react";
import type { ComponentPropsWithoutRef, ElementType } from "react";

type Props<T extends ElementType = "button"> = {
  as?: T;
  size?: ButtonSize;
  variant?: ButtonVariant;
  colorScheme?: ButtonColorScheme;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showText?: boolean;
  css?: SerializedStyles;
} & Omit<ComponentPropsWithoutRef<T>, "css" | "as">;

export const Button = <T extends ElementType = "button">({
  as,
  size = "md",
  variant = "solid",
  colorScheme = "primary",
  children,
  leftIcon,
  rightIcon,
  showText = true,
  css: customCss,
  ...rest
}: Props<T>) => {
  const Component = as ?? "button";
  const buttonStyles = getButtonStyles(size, variant, colorScheme);
  const iconStyles = getIconStyle(size);

  return (
    <Component css={[buttonStyles, customCss]} {...rest}>
      {leftIcon && <span css={iconStyles}>{leftIcon}</span>}
      {showText && <span>{children}</span>}
      {rightIcon && <span css={iconStyles}>{rightIcon}</span>}
    </Component>
  );
};
